import { afterNextRender, Directive, ElementRef, inject, Input } from '@angular/core';

/**
 * Macht ein Bild per Ziehen 360° drehbar (Frame-Sequenz). Erwartet ein <img>
 * als Kind. Beim ersten Sichtbarwerden dreht sich das Modell kurz von selbst,
 * um zu signalisieren, dass man es drehen kann. Läuft nur im Browser.
 */
@Directive({
  selector: '[mb360]',
  standalone: true,
})
export class Mb360Directive {
  private readonly host = inject(ElementRef<HTMLElement>);
  @Input('mb360') frames: string[] = [];

  private idx = 0;
  private interacted = false;

  constructor() {
    afterNextRender(() => {
      const el = this.host.nativeElement;
      const img = el.querySelector('img') as HTMLImageElement | null;
      if (!img || this.frames.length < 2) return;

      this.frames.forEach((src) => {
        const i = new Image();
        i.src = src;
      });

      el.style.cursor = 'grab';
      el.style.touchAction = 'pan-y';

      let dragging = false;
      let startX = 0;
      let startIdx = 0;

      const setFrame = (i: number) => {
        const n = this.frames.length;
        this.idx = ((i % n) + n) % n;
        img.src = this.frames[this.idx];
      };

      const down = (x: number) => {
        this.interacted = true;
        dragging = true;
        startX = x;
        startIdx = this.idx;
        el.style.cursor = 'grabbing';
        el.classList.add('is-dragging');
      };
      const move = (x: number) => {
        if (!dragging) return;
        const dx = x - startX;
        const step = el.clientWidth / this.frames.length;
        setFrame(startIdx + Math.round(dx / step));
      };
      const up = () => {
        dragging = false;
        el.style.cursor = 'grab';
        el.classList.remove('is-dragging');
      };

      el.addEventListener('pointerdown', (e: PointerEvent) => {
        el.setPointerCapture?.(e.pointerId);
        down(e.clientX);
      });
      el.addEventListener('pointermove', (e: PointerEvent) => move(e.clientX));
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      el.addEventListener('pointerleave', up);

      // Auto-Demo beim ersten Sichtbarwerden
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (!entry.isIntersecting) continue;
              io.unobserve(el);
              this.demo(setFrame);
            }
          },
          { threshold: 0.5 }
        );
        io.observe(el);
      }
    });
  }

  /** Sanfte Hin-und-zurück-Drehung als Hinweis. */
  private demo(setFrame: (i: number) => void) {
    const peak = Math.min(9, this.frames.length - 1);
    const total = 1500;
    const start = performance.now();
    const tick = (now: number) => {
      if (this.interacted) return;
      const t = Math.min(1, (now - start) / total);
      // 0 -> peak -> 0 (Sinus)
      const f = Math.round(Math.sin(t * Math.PI) * peak);
      setFrame(f);
      if (t < 1) requestAnimationFrame(tick);
      else setFrame(0);
    };
    requestAnimationFrame(tick);
  }
}
